// Copyright (c) 2026 Goffy. All Rights Reserved. This file belongs to Goffy.

import * as holeOrder from '@/utils/holeOrder';

import type { HoleEntry } from './types';

export type MissingPreviousScoresInput = {
  /** The player whose score was just entered. */
  player: { id: string; name: string };
  /** The hole that score was entered on; only holes played before it are checked. */
  hole: number;
  startHole: number | null | undefined;
  holesCount: number;
  /** Every hole entry the client holds for the round. */
  entries: ReadonlyArray<HoleEntry>;
  /**
   * Holes this device has already scored for the player in this session. A score is only in
   * `entries` once the server has echoed it back, so without this a player scored on two holes
   * in quick succession warns about the first one.
   */
  locallyScoredHoles?: ReadonlySet<number>;
};

/**
 * Entries name the player by id or by name depending on where they came from, so the same
 * three-way match `isHoleSettled` uses is what decides whether an entry belongs to a player.
 */
const entryBelongsToPlayer = (entry: HoleEntry, player: { id: string; name: string }): boolean => {
  if (entry.playerId === player.id) return true;
  if (entry.playerName === player.id) return true;
  return (entry.playerName ?? '').trim().toLowerCase() === player.name.trim().toLowerCase();
};

/** A pickup is a finished hole with no stroke count, so it is scored for this purpose. */
const isScored = (entry: HoleEntry): boolean =>
  entry.pickup === true || (entry.grossStrokes !== null && entry.grossStrokes !== undefined);

/**
 * The holes played before `hole` that hold no score for `player`, in play order.
 *
 * Play order rather than hole number: a round starting on hole 10 plays 10..18 then 1..9, so on
 * hole 3 the holes "before" it include 15, and hole 5 is still ahead.
 */
export function findMissingPreviousHoles(input: MissingPreviousScoresInput): number[] {
  const { player, hole, startHole, holesCount, entries, locallyScoredHoles } = input;
  if (holesCount <= 0) return [];
  const currentIndex = holeOrder.playIndex(hole, startHole, holesCount);
  return holeOrder
    .playOrder(startHole, holesCount)
    .slice(0, Math.max(currentIndex, 0))
    .filter((previous) => {
      if (locallyScoredHoles?.has(previous)) return false;
      return !entries.some(
        (entry) => entry.holeNumber === previous && entryBelongsToPlayer(entry, player) && isScored(entry)
      );
    });
}
